import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Address,
  AddressTypes,
  ApiAddress,
  CitySearchResponse,
  ECategoryOfWarehouse,
  IAddressFilterParam,
  StreetSearchResponse,
  WarehouseResult,
  WarehouseTypeName,
} from '@models/address.model';
import { AddressContactPersonGeneralSaveResponse } from '@models/address.service.models';
import { Contact } from '@models/contact.model';
import { IListData } from '@models/iapi';
import { Locality } from '@models/locality.model';
import { Street } from '@models/street.model';
import { AddressSettings } from '@shared/enums/address.enums';
import * as _ from 'lodash';
import { from, map, Observable, switchMap } from 'rxjs';
import { ApiMethods } from './api/apiMethods';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { NoRestApiHelper } from './no-rest-api.helper';

@Injectable({
  providedIn: 'root',
})
export class AddressService {

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private noRestApiHelper: NoRestApiHelper,
    private authService: AuthService,
  ) {
  }

  get user() {
    return this.authService.user;
  }

  getCities(
    filter: any,
    page = 1,
    limit = 100,
  ): Observable<CitySearchResponse> {
    const postObject: any = {
      system: this.configService.get('system'),
      modelName: 'Address',
      calledMethod: 'searchSettlements',
      methodProperties: {
        CityName: filter.name,
        ...(filter.settlementRef ? { SettlementRef: filter.settlementRef } : {}),
        Limit: limit,
        Page: page,
      },
    };
    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, postObject)
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response.data.data[0] as CitySearchResponse),
      );
  }

  getWarehouses(
    cityRef: string,
    typeOfWarehouseRef?: string,
    warehouseForAgent?: boolean,
    forSender?: boolean,
    CategoryOfWarehouse?: ECategoryOfWarehouse,
    searchQuery?: string,
  ): Observable<WarehouseResult[]> {
    return this._getWarehouses({
      CityRef: cityRef,
      ...(warehouseForAgent !== undefined
        ? { WarehouseForAgent: warehouseForAgent }
        : {}),
      ...(CategoryOfWarehouse ? { CategoryOfWarehouse } : {}),
      ...(forSender ? { PostomatFor: 'Sender' } : {}),
      ...(typeOfWarehouseRef ? { TypeOfWarehouseRef: typeOfWarehouseRef } : {}),
      ...(searchQuery ? { FindByString: searchQuery } : {}),
    });
  }

  getStreets(
    filter: any,
    page = 1,
    limit = 10,
  ): Observable<StreetSearchResponse> {
    const postObject = {
      system: this.configService.get('system'),
      modelName: 'Address',
      calledMethod: 'searchSettlementStreets',
      methodProperties: {
        StreetName: filter.name,
        SettlementRef: filter.localityRef,
        Limit: limit,
        Page: page,
      },
    };
    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, postObject)
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response.data.data[0] as StreetSearchResponse),
      );
  }

  getOneAddress(ref: string, contactRef: string = null): Observable<any> {
    const postObject = {
      system: this.configService.get('system'),
      modelName: 'AddressContactPersonGeneral',
      calledMethod: 'getAddressByRef',
      methodProperties: {
        ContactPersonRef: contactRef,
        Ref: ref,
      },
    };
    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, postObject)
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response),
      );
  }

  addAddressToContact(address: Partial<ApiAddress>, contactRef: string): Observable<AddressContactPersonGeneralSaveResponse> {
    const postObject = {
      system: this.configService.get('system'),
      modelName: 'AddressContactPersonGeneral',
      calledMethod: ApiMethods.save,
      methodProperties: {
        ContactPersonRef: contactRef,
        ...this.mapAddressToMethodProperties(address),
      },
    };

    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, postObject)
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response.data.data[0]),
      );
  }

  getAddresses(contactRef: string, page = 1, limit = 20) {
    const postObject = {
      system: this.configService.get('system'),
      modelName: 'AddressContactPersonGeneral',
      calledMethod: 'getAddresses',
      methodProperties: {
        Page: page - 1,
        limit,
        ContactPersonRef: contactRef,
      },
    };

    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, postObject)
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response.data.data[0]),
      );
  }

  mapAddressToMethodProperties(address: Partial<ApiAddress>): ApiAddress {
    const methodProperties: any = {};

    methodProperties.SettlementRef = address.SettlementRef;
    methodProperties.AddressRef = address.AddressRef;
    methodProperties.AddressType =
      address.AddressType === AddressTypes.DOORS
        ? AddressTypes.DOORS
        : AddressTypes.WAREHOUSE;
    if (methodProperties.AddressType === AddressTypes.DOORS) {
      methodProperties.BuildingNumber = address.BuildingNumber;
      methodProperties.Flat = address.Flat;
      methodProperties.Floor = address.Floor;
      methodProperties.Note = address.Note;
      methodProperties.General = address.General;
    }

    return methodProperties as ApiAddress;
  }

  getList(
    filter: IAddressFilterParam = {},
    page = 1,
    limit = 20,
  ): Observable<IListData<Address>> {
    let request: Observable<any>;

    if (filter.onlyAdresses || filter.onlyWarehouse || filter.isNoPostomat) {
      limit = 1000;
    }

    if (!filter.contactId) {
      request = this.http
        .post(`${this.configService.get('apiUrl')}json/`, {
          system: this.configService.get('system'),
          modelName: 'ContactPersonGeneral',
          calledMethod: 'getContactPersonsList',
          methodProperties: {
            Page: page - 1,
            limit,
            CounterpartyRef:
              this.authService.user && this.authService.user?.contragent?.id,
          },
        })
        .pipe(
          switchMap((data: any) => {
            filter.contactId = data.data.data[0].Ref;

            return this.http.post(`${this.configService.get('apiUrl')}json/`, {
              system: this.configService.get('system'),
              modelName: 'AddressContactPersonGeneral',
              calledMethod: 'getAddresses',
              methodProperties: {
                Page: page - 1,
                limit,
                ContactPersonRef: filter.contactId,
                CityRef: filter.cityId,
              },
            });
          }),
        );
    } else {
      request = this.http.post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'AddressContactPersonGeneral',
        calledMethod: 'getAddresses',
        methodProperties: {
          Page: page - 1,
          limit,
          ContactPersonRef: filter.contactId,
          CityRef: filter.cityId,
        },
      });
    }

    return request.pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((data) => this._bindListToModel(data, filter)),
    );
  }

  getOne(id: string, contactId: string = null): Observable<Address> {
    let promise: any;

    if (!contactId) {
      promise = this.http
        .post(`${this.configService.get('apiUrl')}json/`, {
          system: this.configService.get('system'),
          modelName: 'ContactPersonGeneral',
          calledMethod: 'getContactPersonsList',
          methodProperties: {
            Page: 1,
            limit: 10,
            CounterpartyRef:
              this.authService.user && this.authService.user.contragent.id,
          },
        })
        .pipe(
          switchMap((data: any) => {
            contactId = data.data.data[0].Ref;

            return this.http.post(`${this.configService.get('apiUrl')}json/`, {
              system: this.configService.get('system'),
              modelName: 'AddressContactPersonGeneral',
              calledMethod: 'getAddressByRef',
              methodProperties: {
                ContactPersonRef: contactId,
                Ref: id,
              },
            });
          }),
        );
    } else {
      promise = this.http.post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'AddressContactPersonGeneral',
        calledMethod: 'getAddressByRef',
        methodProperties: {
          ContactPersonRef: contactId,
          Ref: id,
        },
      });
    }

    return promise.pipe(
      map((data) => this.noRestApiHelper.checkErrors(data)),
      map((data) => this._bindOneToModel(data)),
    );
  }

  removeAddress(contactRef: string, refAddress: string): Observable<Address> {
    const addPromise: any = this.http.post(
      `${this.configService.get('apiUrl')}json/`,
      {
        system: this.configService.get('system'),
        modelName: 'AddressContactPersonGeneral',
        calledMethod: 'delete',
        methodProperties: {
          Ref: refAddress,
          ContactPersonRef: contactRef,
        },
      },
    );

    return from(addPromise).pipe(
      map((data) => this.noRestApiHelper.checkErrors(data)),
      map((data) => this._bindOneToModel(data)),
    );
  }

  _detectTypeWarehouse(idType: string): WarehouseTypeName {
    switch (idType) {
      case '6f8c7162-4b72-4b0a-88e5-906948c6a92f':
        return 'NEW_POST';
      case '9a68df70-0267-42a8-bb5c-37f427e36ee4':
        return 'NEW_POST';
      case '841339c7-591a-42e2-8233-7a0a00f0ed6f':
        return 'NEW_POST';
      case '95dc212d-479c-4ffb-a8ab-8c1b9073d0bc':
        return 'POSTBOX';
      case AddressSettings.POSTBOX_TYPE:
        return 'POSTBOX';
      case 'cab18137-df1b-472d-8737-22dd1d18b51d':
        return 'IN_POST';
      default:
        return 'OTHER';
    }
  }

  protected _bindListToModel(
    data: any,
    filter: IAddressFilterParam,
  ): IListData<Address> {
    let addresses: any[] = [];
    if (!filter.onlyAdresses) {
      addresses.push(...data.data.data[0].Warehouses);
    }
    if (!filter.onlyWarehouse) {
      addresses.push(...data.data.data[0].Addresses);
    }

    if (filter.isNoPostomat) {
      addresses = _.filter(
        addresses,
        (address) =>
          this._detectTypeWarehouse(address.TypeOfWarehouse) !== 'POSTBOX',
      );
    }

    return {
      list: _.map(addresses, (el: any) => {
        const address = new Address(el.Ref);

        address.street = new Street(el.StreetRef);
        address.street.name = el.StreetDescription;
        address.street.locality = new Locality(el.CityRef || el.SettlementRef);
        address.street.locality.id2 = el.SettlementRef;
        address.street.locality.name =
          el.SettlementDescription || el.CityDescription || el.CityRef;
        address.street.locality.typeName = el.Type;
        address.city = address.street.locality;
        address.building = el.BuildingDescription || el.BuildingNumber;
        address.street.type = el.StreetsType;
        address.flat = el.Flat;
        address.warehouseNumber = el.WarehouseNumber;
        address.note = el.Note;
        address.noteAddressRecipient = el.NoteAddressRecipient;

        if (el.PlaceMaxWeightAllowed && el.PlaceMaxWeightAllowed !== '0') {
          address.placeMaxWeightAllowed = +el.PlaceMaxWeightAllowed;
        }

        if (el.TotalMaxWeightAllowed && el.TotalMaxWeightAllowed !== '0') {
          address.totalMaxWeightAllowed = +el.TotalMaxWeightAllowed;
        }

        address.description = el.Description || el.AddressDescription;
        if (el.AddressDescription && el.CityDescription) {
          address.description = address.description + ', ' + el.CityDescription;
        }

        address.type =
          address.street && address.street.id ? 'Doors' : 'Warehouse';
        address.typeWarehouse = this._detectTypeWarehouse(el.TypeOfWarehouse);

        // address.description = address.description.replace('Відділення ', '').replace('відділення ', '');
        return address;
      }),
      total: 100,
    };
  }

  protected _bindOneToModel(response: any): Address {
    let address: Address;

    if (response.data.success) {
      const el: any = response.data.data[0];

      address = new Address(el.Ref);
      address.street = new Street(el.StreetRef);
      address.street.name = el.StreetDescription;
      address.street.description = el.StreetDescription;
      address.street.locality = new Locality(el.CityRef);
      address.street.locality.id2 = el.SettlementRef;
      address.street.locality.name =
        el.SettlementDescription || el.CityDescription;
      address.street.locality.description = address.street.locality.name;
      address.street.locality.typeName = el.Type;
      address.city = address.street.locality;
      address.building = el.BuildingNumber;
      address.flat = el.Flat;
      address.note = el.Note;
      address.city = address.street.locality;
      address.noteAddressRecipient = el.NoteAddressRecipient;
      address.type = el.AddressType;

      if (!address.city.warehousesCount && address.type === 'Warehouse') {
        address.city.warehousesCount = 1;
      }

      address.warehouseNumber =
        el.WarehouseNumber ||
        (el.TypeOfWarehouse && 'None kostil need from api');

      address.description =
        el.Description ||
        (el.AddressDescription
          ? el.AddressDescription +
          (el.CityDescription ? ', ' + el.CityDescription : '')
          : '');
      address.contactPerson = new Contact(null, null, el.ContactPersonRef);

      if (el.TypeOfWarehouse) {
        address.typeWarehouse = this._detectTypeWarehouse(el.TypeOfWarehouse);
      }

      if (address.type === 'Warehouse') {
        delete address.street;
      }

      let maxWeigth = parseFloat(el.TotalMaxWeightAllowed);
      if (maxWeigth) {
        address.totalMaxWeightAllowed = maxWeigth;
      }

      maxWeigth = parseFloat(el.PlaceMaxWeightAllowed);
      if (maxWeigth) {
        address.placeMaxWeightAllowed = maxWeigth;
      }
    }

    return address;
  }

  private _getWarehouses(
    methodProperties: object,
  ): Observable<WarehouseResult[]> {
    return this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'AddressGeneral',
        calledMethod: ApiMethods.getWarehouses,
        methodProperties,
      })
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response.data.data as WarehouseResult[]),
      );
  }
}
